import { useMemo, useState } from 'react';
import type { SiteDetail } from '../lib/api';
import { Button, Card, Select } from '../components/ui';

/**
 * Fertiger Auftrag fuer die KI des Kunden.
 *
 * Hintergrund: die meisten Kunden bauen ihre Website heute nicht mehr selbst,
 * sondern lassen sie von einer KI bauen, bei Replit, Lovable, Cursor oder im
 * Chat. Denen ein Snippet und drei Saetze Anleitung zu geben, heisst, dass sie
 * die Anleitung selbst in einen Auftrag uebersetzen muessen, und genau dabei
 * geht die wichtigste Regel verloren: das Script muss vor allen anderen
 * stehen. Steht es weiter unten, feuern die Tags, bevor blockiert wird, und
 * das Banner ist eine Attrappe.
 *
 * Deshalb hier ein Text, der schon alles enthaelt: die Kennung dieser
 * Website, die Reihenfolge, die Stolperfallen und die Pruefung am Ende.
 *
 * Drei Bauarten, weil sich die Arbeit echt unterscheidet. Die Fallstricke der
 * Single Page Application sind keine Theorie, sie stammen aus dem Einbau auf
 * ceo-nikolaidis.de am 2026-10-11.
 */

type Bauart = 'statisch' | 'spa' | 'wordpress';

const BAUARTEN: Array<{ wert: Bauart; label: string; hinweis: string }> = [
  {
    wert: 'statisch',
    label: 'Statische Website aus HTML-Dateien',
    hinweis: 'Jede .html-Datei wird einzeln angefasst.',
  },
  {
    wert: 'spa',
    label: 'React, Vue, Svelte, Next, Replit-App',
    hinweis: 'Eine index.html, die Unterseiten entstehen im Browser.',
  },
  {
    wert: 'wordpress',
    label: 'WordPress',
    hinweis: 'Einbau im Theme oder über ein Code-Plugin.',
  },
];

export function PromptKarte({ site, snippet }: { site: SiteDetail; snippet: string }) {
  const [bauart, setBauart] = useState<Bauart>('statisch');
  const [kopiert, setKopiert] = useState(false);

  const prompt = useMemo(() => bauePrompt(site, snippet, bauart), [site, snippet, bauart]);

  async function kopieren() {
    await navigator.clipboard.writeText(prompt).catch(() => undefined);
    setKopiert(true);
    window.setTimeout(() => setKopiert(false), 2500);
  }

  const gewaehlt = BAUARTEN.find((b) => b.wert === bauart)!;

  return (
    <Card>
      <h2 className="text-lg font-semibold tracking-tight">Von einer KI einbauen lassen</h2>
      <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-muted)]">
        Dieser Text enthält bereits die Kennung dieser Website und alles, worauf es beim Einbau ankommt. Kopieren und
        der KI geben, die die Website betreut, etwa dem Agenten bei Replit oder Lovable. Weitergeben ist unbedenklich:
        die Kennung steht ohnehin im Quelltext jeder Seite.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="w-full sm:max-w-xs">
          <Select
            label="Wie ist die Website gebaut?"
            value={bauart}
            onChange={(e) => setBauart(e.target.value as Bauart)}
          >
            {BAUARTEN.map((b) => (
              <option key={b.wert} value={b.wert}>
                {b.label}
              </option>
            ))}
          </Select>
        </div>
        <p className="text-xs text-[var(--text-muted)] sm:pb-3">{gewaehlt.hinweis}</p>
      </div>

      <pre className="tabular mt-4 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-ink-900 p-3.5 text-xs leading-relaxed text-brand-100">
        {prompt}
      </pre>

      <Button size="sm" variant="secondary" className="mt-2" onClick={kopieren}>
        {kopiert ? 'Kopiert' : 'Prompt kopieren'}
      </Button>
    </Card>
  );
}

function bauePrompt(site: SiteDetail, snippet: string, bauart: Bauart): string {
  // Das Snippet kommt fertig von der Anwendung und traegt die richtige
  // oeffentliche Adresse. Selbst zusammenbauen hiesse, sie ein zweites Mal
  // zu erraten, und beim naechsten Umzug waere genau diese Stelle vergessen.
  const loader = snippet.trim();

  const teile: string[] = [
    `Baue auf der Website ${site.domain} die Einwilligungsverwaltung ilovecookies ein.`,
    '',
    'DAS SCRIPT',
    '',
    loader,
    '',
    'Die Kennung in der Adresse gehört zu dieser Website. Verändere sie nicht und erfinde keine andere.',
    '',
    'DIE WICHTIGSTE REGEL',
    '',
    'Das Script muss das erste Script im <head> sein, vor Google Tag Manager, vor Analytics, vor',
    'jedem Pixel und vor dem eigenen Anwendungscode. Es ersetzt document.createElement und fängt',
    'damit alle später erzeugten Skripte ab. Steht es weiter unten, laufen die Tags schon, bevor',
    'blockiert werden kann, und das Banner ist eine Attrappe.',
    '',
    'Lade es synchron. Kein async, kein defer, kein dynamisches Nachladen, kein Einbau über eine',
    'Komponente oder useEffect. Es gehört direkt ins HTML.',
    '',
  ];

  teile.push(...bauartTeil(bauart, loader));

  teile.push(
    'DIE ZWEI WEITEREN BAUSTEINE',
    '',
    '1. Auf der Datenschutzseite, an der Stelle, wo die Cookie-Übersicht stehen soll:',
    '',
    '<div data-ilc-declaration></div>',
    '',
    '   Der Platzhalter füllt sich von selbst mit allen gefundenen Cookies, mit Zweck, Anbieter',
    '   und Laufzeit. Er bleibt leer, solange noch kein Scan gelaufen ist, das ist normal.',
    '',
    '2. In die Fußzeile jeder Seite:',
    '',
    '<a href="#" data-ilc-open>Cookie-Einstellungen</a>',
    '',
    '   Das ist keine Zierde, sondern Pflicht: der Widerruf muss so einfach sein wie die',
    '   Einwilligung (Art. 7 Abs. 3 DSGVO). Text und Gestaltung darfst du anpassen, das Attribut',
    '   data-ilc-open muss bleiben. Ein <button> tut es auch.',
    '',
    'WAS DU NICHT TUN SOLLST',
    '',
    '- Baue keine eigene Einwilligungslogik und kein zweites Banner. Das übernimmt der Loader.',
    '- Entferne Google Tag Manager, Analytics oder Pixel nicht und packe sie nicht in eigene',
    '  Bedingungen. Sie werden automatisch zurückgehalten und nach der Zustimmung nachgeladen.',
    '- Entferne ein vorhandenes anderes Einwilligungstool vollständig (Cookiebot, Usercentrics,',
    '  Borlabs, CookieYes, Complianz, Real Cookie Banner). Zwei Banner gleichzeitig blockieren',
    '  sich gegenseitig und zeigen dem Besucher zwei widersprüchliche Abfragen.',
    '- Ändere die Kennung nicht und kopiere sie nicht von einer anderen Website.',
    '',
    'ETWAS, DAS SICH NICHT SICHER BLOCKIEREN LÄSST',
    '',
    'Ein <script src="..."> , das fest im HTML steht und VOR unserem Loader geladen wird, lässt',
    'sich nicht mehr aufhalten. Wenn du so etwas nicht nach hinten verschieben kannst, markiere es',
    'stattdessen von Hand:',
    '',
    '<script type="text/plain" data-ilc="marketing" src="..."></script>',
    '',
    'Mögliche Kategorien: necessary, preferences, statistics, marketing. Der Loader startet das',
    'Script erst, wenn für diese Kategorie eine Einwilligung vorliegt.',
    '',
    'ZUM SCHLUSS PRÜFEN',
    '',
    `1. Steht ${'<script id="ilovecookies">'} auf jeder Seite im <head> vor allen anderen Scripten?`,
    '2. Erscheint das Banner beim ersten Aufruf?',
    '3. Wird vor einer Zustimmung wirklich keine Verbindung zu googletagmanager.com oder',
    '   google-analytics.com aufgebaut? Im Netzwerk-Tab der Entwicklerwerkzeuge nachsehen.',
    '4. Öffnet der Link in der Fußzeile das Banner erneut?',
    '5. Ist nur noch ein einziges Einwilligungstool eingebunden?',
  );

  return teile.join('\n');
}

function bauartTeil(bauart: Bauart, loader: string): string[] {
  if (bauart === 'statisch') {
    return [
      'WO GENAU',
      '',
      'In JEDE .html-Datei des Projekts, auch in Unterordnern, auch in die 404-Seite. Setze das',
      'Script direkt hinter <meta charset="..."> und vor alles andere.',
      '',
      'Vergiss keine Datei. Eine Seite ohne das Script setzt beim Besuch Cookies ohne Einwilligung,',
      'und der Besucher landet dort oft über Google, nicht über die Startseite.',
      '',
    ];
  }

  if (bauart === 'spa') {
    return [
      'WO GENAU',
      '',
      'In die index.html des Projekts, also die Datei, aus der gebaut wird, nicht in eine',
      'React- oder Vue-Komponente. Bei Vite liegt sie meist unter index.html oder client/index.html,',
      'bei Next.js gehört sie in das Dokument-Grundgerüst.',
      '',
      'Drei Dinge, die bei dieser Bauart regelmäßig schiefgehen:',
      '',
      '1. Die Unterseiten entstehen erst im Browser. Ruft jemand /datenschutz direkt auf oder lädt',
      '   die Seite dort neu, sucht der Webserver eine Datei dieses Namens und antwortet mit 404.',
      '   Sorge dafür, dass der Server für unbekannte Adressen die index.html ausliefert. Auf',
      '   Apache über eine .htaccess mit mod_rewrite, bei nginx über try_files, bei Netlify oder',
      '   Vercel über die jeweilige Weiterleitungsregel.',
      '',
      '2. Wenn im HTML eine eigene Bedingung steht, die Tag Manager oder Analytics startet, hänge',
      '   window.IloveCookies an diese Bedingung an. Diese Variable existiert erst, wenn der Loader',
      '   wirklich geladen wurde. Fällt er aus, läuft dann nichts, statt ohne Einwilligung zu messen.',
      '',
      '3. Der Platzhalter für die Cookie-Erklärung darf ruhig in einer Komponente stehen, die erst',
      '   beim Aufruf der Datenschutzseite entsteht. Der Loader bemerkt ihn auch nachträglich.',
      '',
      `Das Script gehört unverändert in den <head>: ${loader}`,
      '',
    ];
  }

  return [
    'WO GENAU',
    '',
    'Zwei Wege, einer reicht:',
    '',
    'a) Im Theme: in header.php unmittelbar nach dem öffnenden <head>, noch vor wp_head().',
    '   Nur in einem Child-Theme, sonst ist es beim nächsten Theme-Update weg.',
    '',
    'b) Über ein Code-Plugin wie WPCode oder Head & Footer Code: als Code im Kopfbereich mit der',
    '   höchsten Priorität, damit es vor allen anderen Einträgen steht.',
    '',
    'Prüfe danach im Quelltext der fertigen Seite, ob wirklich kein anderes <script> davor steht.',
    'Caching- und Optimierungs-Plugins wie WP Rocket oder Autoptimize verschieben Skripte gern',
    'nach unten oder fassen sie zusammen. Nimm dieses Script von der Optimierung aus und leere',
    'anschließend den Cache.',
    '',
    'Vorhandene Einwilligungs-Plugins (Borlabs Cookie, Complianz, Real Cookie Banner, CookieYes)',
    'deaktivieren und deren Reste entfernen.',
    '',
  ];
}
